import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import {
  createAPIKeyConnection,
  deleteConnection,
  generateConnectionTOTPCode,
  listCatalog,
  listConnections,
  listOAuthClients,
  startConnect,
  updateConnectionTOTP,
  type Connection,
  type ConnectorDefinition,
} from './api';

export function ConnectorsCatalog({ orgId }: { orgId: string }) {
  const catalog = useQuery({
    queryKey: ['connector-catalog', orgId],
    queryFn: () => listCatalog(orgId),
    enabled: !!orgId,
  });
  const connections = useQuery({
    queryKey: ['connections', orgId],
    queryFn: () => listConnections(orgId),
    enabled: !!orgId,
  });
  const clients = useQuery({
    queryKey: ['oauth-clients', orgId],
    queryFn: () => listOAuthClients(orgId),
    enabled: !!orgId,
  });

  const connectedClients = new Set((clients.data ?? []).map((c) => c.vendor));
  const byDefinition = groupConnections(connections.data ?? []);

  return (
    <Card>
      <CardHeader
        title="Catalog"
        description="Click Connect on a card to grant Sigil access to that account. You can have multiple connections of the same kind (e.g. two Google Drive accounts)."
      />
      <CardBody className="space-y-4">
        {catalog.isLoading && <p className="text-sm text-ink-tertiary">Loading catalog…</p>}
        {catalog.data && catalog.data.length === 0 && (
          <p className="text-sm text-ink-tertiary">
            No connectors yet. They ship with backend updates — there's nothing to enable here for now.
          </p>
        )}
        {catalog.data && catalog.data.length > 0 && (
          <div className="grid gap-3 sm:grid-cols-2">
            {catalog.data.map((d) => (
              <DefinitionCard
                key={d.slug}
                orgId={orgId}
                def={d}
                hasOAuthClient={connectedClients.has(d.vendor)}
                connections={byDefinition[d.slug] ?? []}
              />
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function DefinitionCard({
  orgId,
  def,
  hasOAuthClient,
  connections,
}: {
  orgId: string;
  def: ConnectorDefinition;
  hasOAuthClient: boolean;
  connections: Connection[];
}) {
  const connect = useMutation({
    mutationFn: () => startConnect(orgId, def.slug, '/connectors'),
    onSuccess: ({ authorize_url }) => {
      window.location.href = authorize_url;
    },
  });

  return (
    <div className="rounded-lg border border-line-soft bg-surface-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="truncate text-sm font-semibold text-ink-primary">{def.name}</h3>
            <StatusBadge status={def.status} />
          </div>
          <p className="mt-1 text-xs text-ink-secondary">{def.description}</p>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-1">
        {def.tools.slice(0, 6).map((t) => (
          <span
            key={t.name}
            title={t.description}
            className="rounded border border-line-soft bg-surface-soft px-1.5 py-0.5 font-mono text-[10px] text-ink-secondary"
          >
            {t.name}
          </span>
        ))}
        {def.tools.length > 6 && (
          <span className="font-mono text-[10px] text-ink-tertiary">+{def.tools.length - 6} more</span>
        )}
      </div>

      {connections.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {connections.map((c) => (
            <ConnectionRow
              key={c.id}
              orgId={orgId}
              conn={c}
              showTOTP={def.credential_type === 'api_key'}
            />
          ))}
        </ul>
      )}

      {def.credential_type === 'api_key' ? (
        <APIKeyConnectForm orgId={orgId} def={def} existing={connections.length > 0} />
      ) : (
        <div className="mt-3 flex items-center justify-between">
          {!hasOAuthClient && (
            <p className="text-[11px] text-amber-700">
              Add {def.vendor} OAuth credentials first.
            </p>
          )}
          <button
            type="button"
            onClick={() => connect.mutate()}
            disabled={!hasOAuthClient || connect.isPending}
            className="ml-auto rounded-md bg-bit2me px-3 py-1.5 text-xs font-medium text-white disabled:bg-ink-muted disabled:cursor-not-allowed"
          >
            {connect.isPending ? 'Redirecting…' : connections.length > 0 ? '+ Connect another' : 'Connect'}
          </button>
        </div>
      )}
    </div>
  );
}

function ConnectionRow({
  orgId,
  conn,
  showTOTP,
}: {
  orgId: string;
  conn: Connection;
  showTOTP: boolean;
}) {
  const qc = useQueryClient();
  const [editTOTP, setEditTOTP] = useState(false);
  const [seed, setSeed] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [code, setCode] = useState<{ value: string; expiresAt: number } | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!code) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [code]);

  const save = useMutation({
    mutationFn: () => updateConnectionTOTP(orgId, conn.id, seed.trim()),
    onSuccess: () => {
      setSeed('');
      setErr(null);
      qc.invalidateQueries({ queryKey: ['connections', orgId] });
      // Immediately show a fresh code so the operator can paste it
      // into Bit2Me's "verify your 2FA" step without leaving Sigil.
      showCode.mutate();
    },
    onError: (e: any) => {
      setErr(e?.response?.data?.error?.message ?? e?.message ?? 'Failed to save TOTP');
    },
  });

  const showCode = useMutation({
    mutationFn: () => generateConnectionTOTPCode(orgId, conn.id),
    onSuccess: (data) => {
      setCode({ value: data.code, expiresAt: new Date(data.expires_at).getTime() });
      setErr(null);
    },
    onError: (e: any) => {
      setErr(
        e?.response?.data?.error?.message ??
          e?.message ??
          'No TOTP seed configured yet — save one first',
      );
    },
  });

  const remainingSec = code ? Math.max(0, Math.ceil((code.expiresAt - now) / 1000)) : 0;

  return (
    <li className="rounded border border-line-soft bg-surface-soft/40 px-2 py-1">
      <div className="flex items-center justify-between gap-2">
        <p className="truncate text-xs text-ink-primary">{conn.account_label}</p>
        <div className="flex shrink-0 items-center gap-2">
          <span className="font-mono text-[10px] uppercase text-ink-tertiary">{conn.status}</span>
          {showTOTP && (
            <button
              type="button"
              onClick={() => setEditTOTP((v) => !v)}
              className="text-[11px] text-bit2me hover:underline"
            >
              {editTOTP ? 'Cancel' : 'TOTP'}
            </button>
          )}
          <button
            type="button"
            onClick={async () => {
              if (window.confirm(`Disconnect ${conn.account_label}?`)) {
                await deleteConnection(orgId, conn.id);
                qc.invalidateQueries({ queryKey: ['connections', orgId] });
              }
            }}
            className="text-[11px] text-danger hover:underline"
          >
            Disconnect
          </button>
        </div>
      </div>
      {editTOTP && (
        <form
          className="mt-2 space-y-1"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <p className="text-[10px] text-ink-secondary">
            Google Authenticator seed (base32, with or without spaces). Paste empty to remove.
          </p>
          <input
            value={seed}
            onChange={(e) => setSeed(e.target.value)}
            placeholder="JBSWY3DPEHPK3PXP…"
            className="w-full rounded border border-line-soft px-2 py-1 font-mono text-[11px]"
          />
          {err && <p className="text-[10px] text-danger">{err}</p>}
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => showCode.mutate()}
              disabled={showCode.isPending}
              className="rounded border border-line-soft px-2 py-1 text-[11px] text-ink-secondary hover:bg-surface-soft"
            >
              {showCode.isPending ? 'Generating…' : 'Show current code'}
            </button>
            <button
              type="submit"
              disabled={save.isPending}
              className="rounded-md bg-bit2me px-3 py-1 text-[11px] font-medium text-white disabled:bg-ink-muted"
            >
              {save.isPending ? 'Saving…' : 'Save TOTP'}
            </button>
          </div>
          {code && (
            <div className="mt-2 flex items-center justify-between rounded border border-ok/30 bg-ok/5 px-2 py-1.5">
              <div>
                <p className="text-[10px] uppercase tracking-eyebrow text-ink-tertiary">
                  Current code
                </p>
                <p
                  className="cursor-pointer font-mono text-base font-semibold text-ink-primary"
                  onClick={() => navigator.clipboard?.writeText(code.value)}
                  title="Click to copy"
                >
                  {code.value.slice(0, 3)} {code.value.slice(3)}
                </p>
              </div>
              <p className="font-mono text-[10px] text-ink-tertiary">
                expires in {remainingSec}s
              </p>
            </div>
          )}
        </form>
      )}
    </li>
  );
}

function APIKeyConnectForm({
  orgId,
  def,
  existing,
}: {
  orgId: string;
  def: ConnectorDefinition;
  existing: boolean;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(!existing);
  const [label, setLabel] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [totpSecret, setTotpSecret] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: () =>
      createAPIKeyConnection(orgId, {
        definition: def.slug,
        label,
        api_key: apiKey,
        api_secret: apiSecret,
        totp_secret: totpSecret || undefined,
      }),
    onSuccess: () => {
      setLabel('');
      setApiKey('');
      setApiSecret('');
      setTotpSecret('');
      setErr(null);
      qc.invalidateQueries({ queryKey: ['connections', orgId] });
      setOpen(false);
    },
    onError: (e: any) => {
      setErr(e?.response?.data?.error?.message ?? e?.message ?? 'Failed to save credentials');
    },
  });

  if (!open) {
    return (
      <div className="mt-3 flex justify-end">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="rounded-md bg-bit2me px-3 py-1.5 text-xs font-medium text-white"
        >
          + Connect another
        </button>
      </div>
    );
  }

  return (
    <form
      className="mt-3 space-y-2 rounded border border-line-soft bg-surface-soft/40 p-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (!label || !apiKey || !apiSecret) {
          setErr('All three fields are required');
          return;
        }
        submit.mutate();
      }}
    >
      <p className="text-[11px] text-ink-secondary">
        Paste a {def.name} API key. The secret is encrypted at rest with KMS — Sigil never displays it back.
      </p>
      <input
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder="Label (e.g. Treasury account)"
        className="w-full rounded border border-line-soft px-2 py-1 text-xs"
      />
      <input
        value={apiKey}
        onChange={(e) => setApiKey(e.target.value)}
        placeholder="API key"
        className="w-full rounded border border-line-soft px-2 py-1 font-mono text-xs"
      />
      <input
        type="password"
        value={apiSecret}
        onChange={(e) => setApiSecret(e.target.value)}
        placeholder="API secret"
        className="w-full rounded border border-line-soft px-2 py-1 font-mono text-xs"
      />
      <input
        type="password"
        value={totpSecret}
        onChange={(e) => setTotpSecret(e.target.value)}
        placeholder="TOTP seed (optional, only needed if 2FA is required on writes)"
        className="w-full rounded border border-line-soft px-2 py-1 font-mono text-xs"
      />
      {err && <p className="text-[11px] text-danger">{err}</p>}
      <div className="flex justify-end gap-2">
        {existing && (
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              setErr(null);
            }}
            className="rounded-md px-3 py-1.5 text-xs text-ink-secondary"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={submit.isPending}
          className="rounded-md bg-bit2me px-3 py-1.5 text-xs font-medium text-white disabled:bg-ink-muted"
        >
          {submit.isPending ? 'Saving…' : 'Save credentials'}
        </button>
      </div>
    </form>
  );
}

function StatusBadge({ status }: { status: string }) {
  const tone =
    status === 'ga'
      ? 'border-ok/30 bg-ok/10 text-ok'
      : status === 'beta'
      ? 'border-amber-200 bg-amber-50 text-amber-700'
      : 'border-line bg-surface-soft text-ink-tertiary';
  return (
    <span
      className={`rounded-full border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-eyebrow ${tone}`}
    >
      {status}
    </span>
  );
}

function groupConnections(items: Connection[]): Record<string, Connection[]> {
  const out: Record<string, Connection[]> = {};
  for (const c of items) {
    out[c.definition_slug] = out[c.definition_slug] ?? [];
    out[c.definition_slug].push(c);
  }
  return out;
}
